import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "@/config/api";
import { getAccessToken } from "@/utils/token_functions";
import { secureAxios } from "@/middleware/encryptionMiddleware";

// Async thunk for creating a manifesto
export const createManifesto = createAsyncThunk(
  "manifesto/create",
  async (manifestoData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();

      const response = await secureAxios.post(
        `${BASE_URL}/v1/manifesto`,
        manifestoData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Async thunk for fetching all manifestos
export const getAllManifestos = createAsyncThunk(
  "manifesto/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAccessToken();

      const response = await secureAxios.get(`${BASE_URL}/v1/manifesto`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchManifestoByAny = createAsyncThunk(
  "manifesto/fetchByAny",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.get(
        `${BASE_URL}/v1/manifesto/by-any/${key}/${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return {
        data: response.data.data,
        total: response.data.data.length, // Include the total count
        isSingleManifesto: key === "_id", // Flag to indicate if we're fetching by ID
      };
    } catch (error) {
      // If it's a 404 error (not found), return an empty array instead of rejecting
      if (error.response?.status === 404) {
        return { data: [], total: 0, isSingleManifesto: false };
      }
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Keep backward compatibility with fetchSingleManifesto
export const fetchSingleManifesto = createAsyncThunk(
  "manifesto/fetchSingle",
  async ({ key, value }, { dispatch }) => {
    const result = await dispatch(fetchManifestoByAny({ key, value })).unwrap();
    return result.data[0];
  }
);

export const updateManifesto = createAsyncThunk(
  "manifesto/update",
  async (manifestoData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.patch(
        `${BASE_URL}/v1/manifesto`,
        {
          _id: manifestoData._id,
          name: manifestoData.name,
          description: manifestoData.description,
          fileUrl: manifestoData.fileUrl,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const deleteManifesto = createAsyncThunk(
  "manifesto/delete",
  async (manifestoId, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.delete(`${BASE_URL}/v1/manifesto`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: [manifestoId],
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

const initialState = {
  manifestoForm: {
    name: "",
    description: "",
    fileUrl: "",
    linkedCampaigns: [],
  },
  manifestos: [], // Array to store all manifestos
  fetchedManifestos: [],
  currentManifesto: null,
  total: 0,
  isLoading: false,
  error: null,
  success: false,
  isOfflineData: false,
};

const manifestoSlice = createSlice({
  name: "manifesto",
  initialState,
  reducers: {
    updateManifestoForm: (state, action) => {
      state.manifestoForm = {
        ...state.manifestoForm,
        ...action.payload,
      };
    },
    resetManifestoForm: (state) => {
      state.manifestoForm = initialState.manifestoForm;
      state.success = false;
    },
    clearManifestoState: () => initialState,
    loadCachedData: (state, action) => {
      return {
        ...state,
        manifestos: action.payload.manifestos || state.manifestos,
        fetchedManifestos:
          action.payload.fetchedManifestos || state.fetchedManifestos,
        currentManifesto:
          action.payload.currentManifesto || state.currentManifesto,
        total: action.payload.total || state.total,
        isLoading: false,
        error: null,
        isOfflineData: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Reducers for createManifesto
      .addCase(createManifesto.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createManifesto.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
        // Don't reset the form here
      })
      .addCase(createManifesto.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Reducers for getAllManifestos
      .addCase(getAllManifestos.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllManifestos.fulfilled, (state, action) => {
        state.isLoading = false;
        state.manifestos = action.payload.data;
        state.error = null;
      })
      .addCase(getAllManifestos.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchManifestoByAny.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchManifestoByAny.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fetchedManifestos = action.payload.data;
        state.total = action.payload.total;

        if (
          action.payload.isSingleManifesto &&
          action.payload.data.length > 0
        ) {
          // If we're fetching by ID, store as currentManifesto
          state.currentManifesto = action.payload.data[0];
        } else {
          state.currentManifesto =
            action.payload.data.length > 0 ? action.payload.data[0] : null;
        }
        state.error = null;
      })
      .addCase(fetchManifestoByAny.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Keep backward compatibility for fetchSingleManifesto
      .addCase(fetchSingleManifesto.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleManifesto.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentManifesto = action.payload;
        state.error = null;
      })
      .addCase(fetchSingleManifesto.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(updateManifesto.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateManifesto.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(updateManifesto.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = false;
      })

      .addCase(deleteManifesto.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteManifesto.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(deleteManifesto.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  updateManifestoForm,
  resetManifestoForm,
  clearManifestoState,
  loadCachedData,
} = manifestoSlice.actions;

export const selectManifestoForm = (state) => state.manifesto.manifestoForm;
export const selectManifestos = (state) => state.manifesto.manifestos;
export const selectFetchedManifestos = (state) =>
  state.manifesto.fetchedManifestos || [];
export const selectManifestoLoading = (state) => state.manifesto.isLoading;
export const selectManifestoError = (state) => state.manifesto.error;
export const selectManifestoSuccess = (state) => state.manifesto.success;
export const selectCurrentManifesto = (state) =>
  state.manifesto.currentManifesto;
export const selectManifestoTotal = (state) => state.manifesto.total || 0;
export const selectIsOfflineData = (state) =>
  state.manifesto.isOfflineData || false;
export default manifestoSlice.reducer;
